/* eslint-disable max-lines */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { injectCurrentUserPreferencesContract } from '@core/account-contracts';
import { AccountSelectors } from '@core/account-data';
import { BillingPlanTier } from '@core/billing-types';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import type { HelpTranslationLinkMapKey } from '@core/help-articles-legacy';
import { I18nService } from '@core/i18n-legacy';
import { ThemedImageTuple } from '@core/theme-util';

import { FrontendOnlyGuardedFeature, GuardedFeature } from './feature-guard.types';
import { PricingAnalyticsFeature, PricingAnalyticsService } from './pricing-analytics.service';

export enum MediaSourceType {
  Image = 'image',
  Video = 'video',
  YoutubeVideo = 'youtube_video',
  fallbackImgSource = 'fallbackImgSource',
}

export type FeatureData = {
  title: string;
  description: string;
  upgradeUrl: string;
  helpUrl?: string;
  helpArticleKey?: HelpTranslationLinkMapKey;
  learnMoreTranslationKey?: string;
  learnMoreUrlByLang?: {
    en: string;
    de: string;
    fr: string;
    nl: string;
  };
  mediaSource: string | ThemedImageTuple;
  mediaSourceType: MediaSourceType;
  fallbackImgSource?: string;
  analytics?: {
    discrimination: () => void;
    cta: () => void;
  };
  noContentWidth?: boolean;
};

@Injectable({ providedIn: 'root' })
export class FeaturesGuardModel {
  private readonly i18n = inject(I18nService);
  private readonly pricingAnalyticsService = inject(PricingAnalyticsService);

  private readonly pp24Enabled = injectDevFlag(DevFlag.PP24Enabled);
  private readonly currentUserPreferencesFacade = injectCurrentUserPreferencesContract();
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private readonly themeName = toSignal(this.currentUserPreferencesFacade.effectiveThemeName$);
  private readonly currentPlanTier = this.store.selectSignal(AccountSelectors.productCodename);

  // Lead has everything from Scale and Launch, Scale has everything from Launch, Legacy is a special case where features need to be listed explicitly
  public readonly planFeatures: { [key: string]: GuardedFeature[] } = {
    [BillingPlanTier.Launch]: [
      GuardedFeature.AcquisitionPaidCampaigns,
      GuardedFeature.OverdueCandidates,
      GuardedFeature.Security,
      GuardedFeature.AuditLogs,
      GuardedFeature.Locations,
    ],
    [BillingPlanTier.Scale]: [
      GuardedFeature.AdjustableRoles,
      GuardedFeature.CustomisableReports,
      GuardedFeature.AutomatedActions,
      GuardedFeature.CustomDomain,
      GuardedFeature.DefaultProfileFields,
      GuardedFeature.EEO,
      GuardedFeature.EvaluationForms,
      GuardedFeature.Multilang,
      GuardedFeature.Pipelines,
      GuardedFeature.Questionnaires,
      GuardedFeature.Scheduler,
      GuardedFeature.OpenQuestions,
      GuardedFeature.SendLater,
      GuardedFeature.SourcingExtension,
      GuardedFeature.Visibility,
      GuardedFeature.Webhooks,
      GuardedFeature.MeetingRooms,
      GuardedFeature.JobScheduler,
      GuardedFeature.ProfilePrint,
      GuardedFeature.FairEvaluations,
      GuardedFeature.NotificationsPerRole,
      GuardedFeature.KnockoutQuestions,
      GuardedFeature.GDPRAutomations,
    ],
    [BillingPlanTier.Lead]: [GuardedFeature.SSO, GuardedFeature.Requisitions],
    [BillingPlanTier.Start]: [
      GuardedFeature.AcquisitionPaidCampaigns,
      GuardedFeature.OverdueCandidates,
      GuardedFeature.Security,
      GuardedFeature.AuditLogs,
      GuardedFeature.AutomatedActions,
      GuardedFeature.AdjustableRoles,
      GuardedFeature.Locations,
      GuardedFeature.GDPRAutomations,
    ],
    [BillingPlanTier.Grow]: [
      GuardedFeature.CustomisableReports,
      GuardedFeature.CustomDomain,
      GuardedFeature.DefaultProfileFields,
      GuardedFeature.EEO,
      GuardedFeature.EvaluationForms,
      GuardedFeature.Multilang,
      GuardedFeature.Pipelines,
      GuardedFeature.Questionnaires,
      GuardedFeature.Scheduler,
      GuardedFeature.OpenQuestions,
      GuardedFeature.SendLater,
      GuardedFeature.SourcingExtension,
      GuardedFeature.Visibility,
      GuardedFeature.Webhooks,
      GuardedFeature.MeetingRooms,
      GuardedFeature.JobScheduler,
      GuardedFeature.ProfilePrint,
      GuardedFeature.NotificationsPerRole,
      GuardedFeature.KnockoutQuestions,
      GuardedFeature.Referrals,
    ],
    [BillingPlanTier.Optimize]: [
      GuardedFeature.FairEvaluations,
      GuardedFeature.SSO,
      GuardedFeature.Requisitions,
    ],
  };

  private _data: { [key: string]: FeatureData };

  constructor() {
    this.currentUserPreferencesFacade.effectiveThemeName$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setData());
  }

  public get data() {
    return this._data;
  }

  // eslint-disable-next-line max-lines-per-function
  private setData(): void {
    this._data = {
      [GuardedFeature.SendLater]: {
        mediaSource: 'assets/images/feature-guard/send-later.svg',
        title: this.i18n.translate('app.mailbox.send_later'),
        description: this.i18n.translate('app.pricing.feature_guard.send_later_desc'),
        helpArticleKey: 'scheduleEmailsToBeSentLater',
        upgradeUrl: '/mailbox/upgrade',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.SendLater,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.SendLater),
        },
      },
      [GuardedFeature.Scheduler]: {
        mediaSource: 'assets/images/feature-guard/scheduler.svg',
        title: this.i18n.translate('app.pricing.plan_picker.features.scheduler'),
        description: this.i18n.translate('app.pricing.feature_guard.scheduler_desc'),
        helpArticleKey: 'scheduleCandidateInterview',
        upgradeUrl: '/settings/upgrade/scheduler',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Scheduler,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Scheduler),
        },
      },
      [GuardedFeature.DefaultProfileFields]: {
        mediaSource: 'assets/images/feature-guard/profile-fields.svg',
        title: this.i18n.translate('app.pricing.feature_guard.profile_fields'),
        description: this.i18n.translate('app.pricing.feature_guard.profile_fields_desc'),
        helpArticleKey: 'manageProfileFields',
        upgradeUrl: '/settings/upgrade/profile_fields',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.ProfileFieldsTemplates,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.ProfileFieldsTemplates,
            ),
        },
      },
      [GuardedFeature.AutomatedActions]: {
        title: this.i18n.translate('app.automated_actions.automated_actions'),
        description: this.i18n.translate('app.pricing.feature_guard.automated_actions_desc'),
        helpArticleKey: 'automatedActions',
        upgradeUrl: '',
        mediaSource: 'assets/images/feature-guard/automated-actions.svg',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.AutomatedActions,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.AutomatedActions,
            ),
        },
      },
      [GuardedFeature.Pipelines]: {
        mediaSource: 'assets/video/feature-upgrade/workflow_templates_pipeline.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.pipelines.title'),
        description: this.i18n.translate('app.pricing.feature_guard.pipelines.description'),
        helpArticleKey: 'pipelineTemplatesBeta',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.PipelineTemplates,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.PipelineTemplates,
            ),
        },
      },
      [GuardedFeature.Multilang]: {
        mediaSource: 'assets/images/feature-guard/multilang.svg',
        title: this.i18n.translate('app.pricing.feature_guard.multilang'),
        description: this.i18n.translate('app.pricing.feature_guard.multilang_desc'),
        helpArticleKey: 'multilang',
        upgradeUrl: '/settings/upgrade/multilang',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Multilang,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Multilang),
        },
      },
      [GuardedFeature.EvaluationForms]: {
        mediaSource: 'assets/images/feature-guard/evaluation-form.svg',
        title: this.i18n.translate('app.pricing.feature_guard.evaluation_forms.title'),
        description: this.i18n.translate('app.pricing.feature_guard.evaluation_forms.description'),
        helpArticleKey: 'detailedCandidateEvaluations',
        upgradeUrl: '/settings/upgrade/evaluation_forms',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.EvaluationForms,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.EvaluationForms,
            ),
        },
      },
      [GuardedFeature.Questionnaires]: {
        mediaSource: 'assets/images/feature-guard/questionnaires.svg',
        title: this.i18n.translate('app.pricing.feature_guard.questionnaires.title'),
        description: this.i18n.translate('app.pricing.feature_guard.questionnaires.description'),
        helpArticleKey: 'sendQuestionnaires',
        upgradeUrl: '/settings/upgrade/questionnaires',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Questionnaires,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.Questionnaires,
            ),
        },
      },
      [GuardedFeature.CustomDomain]: {
        mediaSource: 'assets/images/feature-guard/custom_domain.png',
        title: this.i18n.translate('app.pricing.feature_guard.custom_domain.title'),
        description: this.i18n.translate('app.pricing.feature_guard.custom_domain.description'),
        helpArticleKey: 'customDomain',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.CustomDomain,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.CustomDomain),
        },
      },
      [GuardedFeature.CustomisableReports]: {
        mediaSource: 'assets/video/feature-upgrade/reports.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.advanced_reporting'),
        description: this.i18n.translate('app.pricing.feature_guard.advanced_reporting_desc'),
        learnMoreUrlByLang: {
          en: 'https://blog.recruitee.com/recruitee/product-updates/recruitment-metrics/',
          de: 'https://recruitee.com/predict/recruitment-analytics',
          fr: 'https://recruitee.com/predict/recruitment-analytics',
          nl: 'https://recruitee.com/predict/recruitment-analytics',
        },
        upgradeUrl: '/reports/upgrade',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.AdvancedReports,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.AdvancedReports,
            ),
        },
      },
      [GuardedFeature.SSO]: {
        mediaSource: 'assets/video/feature-upgrade/sso.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.sso.title'),
        description: this.i18n.translate('app.pricing.feature_guard.sso.description'),
        helpArticleKey: 'ssoOverview',
        learnMoreTranslationKey: 'app.pricing.feature_guard.sso.cta',
        upgradeUrl: '/settings/company/upgrade_sso',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.SSO),
          cta: () => this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.SSO),
        },
      },
      [GuardedFeature.Visibility]: {
        mediaSource: 'assets/video/feature-upgrade/visibility.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.visibility.title'),
        description: this.i18n.translate('app.pricing.feature_guard.visibility.description'),
        helpArticleKey: 'visibility',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Visibility,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Visibility),
        },
      },
      [GuardedFeature.AdjustableRoles]: {
        mediaSource: 'assets/images/feature-guard/roles.svg',
        title: this.i18n.translate('app.pricing.feature_guard.adjustable_roles.title'),
        description: this.i18n.translate('app.pricing.feature_guard.adjustable_roles.description'),
        helpArticleKey: 'roles',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Roles),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Roles),
        },
      },
      [GuardedFeature.OverdueCandidates]: {
        title: this.i18n.translate('app.overdue_candidates.overdue_candidate_reminders'),
        description: this.i18n.translate('app.overdue_candidates.feature_guard_desc'),
        helpArticleKey: 'overdueCandidates',
        upgradeUrl: '',
        mediaSource: 'assets/video/feature-upgrade/overdue_candidates.mp4',
        mediaSourceType: MediaSourceType.Video,
        fallbackImgSource: 'assets/images/feature-guard/overdue-candidates.svg',
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.OverdueCandidates,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.OverdueCandidates,
            ),
        },
      },
      [GuardedFeature.OpenQuestions]: {
        mediaSource: 'assets/video/feature-upgrade/workflow_templates_open_questions.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.screening_questions.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.screening_questions.description',
        ),
        helpArticleKey: 'screeningQuestionsTemplates',
        upgradeUrl: '/settings/upgrade/screening_questions',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.ScreeningQuestions,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.ScreeningQuestions,
            ),
        },
      },
      [GuardedFeature.GDPRAutomations]: {
        mediaSource: 'assets/video/feature-upgrade/gdpr_automation.mp4',
        title: this.i18n.translate('app.pricing.feature_guard.gdpr_automations.title'),
        description: this.i18n.translate('app.pricing.feature_guard.gdpr_automations.description'),
        helpArticleKey: 'gdprSettingsAutomationsConsentDeleteRequest',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.GDPRAutomations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.GDPRAutomations,
            ),
        },
      },
      [GuardedFeature.Webhooks]: {
        mediaSource: 'assets/images/feature-guard/webhooks.svg',
        title: this.i18n.translate('app.webhooks.feature_guard.title'),
        description: this.i18n.translate('app.webhooks.feature_guard.description'),
        // TODO: Add correct URL when we have somewhere to point at
        helpUrl: 'https://docs.recruitee.com/reference',
        upgradeUrl: '/settings/upgrade/webhooks',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Webhooks,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Webhooks),
        },
      },
      [GuardedFeature.MeetingRooms]: {
        mediaSource: 'assets/video/feature-upgrade/meeting_rooms.mp4',
        title: this.i18n.translate('app.settings.meeting_rooms.heading'),
        description: this.i18n.translate('app.settings.meeting_rooms.subheading'),
        helpArticleKey: 'meetingRoms',
        upgradeUrl: '/settings/upgrade/meeting_rooms',
        mediaSourceType: MediaSourceType.Video,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.MeetingRooms,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.MeetingRooms),
        },
      },
      [GuardedFeature.JobScheduler]: {
        mediaSource: 'assets/images/feature-guard/job_scheduler.svg',
        title: this.i18n.translate('app.pricing.feature_guard.job_scheduler.title'),
        description: this.i18n.translate('app.pricing.feature_guard.job_scheduler.description'),
        helpArticleKey: 'jobScheduler',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.JobScheduler,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.JobScheduler),
        },
      },
      [GuardedFeature.ProfilePrint]: {
        mediaSource: 'assets/images/feature-guard/profile-print.svg',
        title: this.i18n.translate('app.pricing.feature_guard.profile_print.title'),
        description: this.i18n.translate('app.pricing.feature_guard.profile_print.description'),
        helpArticleKey: 'profilePrint',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.ProfilePrint,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.ProfilePrint),
        },
      },
      [GuardedFeature.Security]: {
        mediaSource: 'assets/images/feature-guard/security.svg',
        title: this.i18n.translate('app.pricing.feature_guard.security.title'),
        description: this.i18n.translate('app.pricing.feature_guard.security.description'),
        helpArticleKey: 'security',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
      },
      [GuardedFeature.EEO]: {
        mediaSource: 'assets/images/feature-guard/eeo.svg',
        title: this.i18n.translate('app.pricing.feature_guard.eeo.title'),
        description: this.i18n.translate('app.pricing.feature_guard.eeo.description'),
        helpArticleKey: 'eeoIntro',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.EEO),
          cta: () => this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.EEO),
        },
      },
      [GuardedFeature.Referrals]: {
        mediaSource: 'https://www.youtube.com/embed/ISmE0d8mz38',
        title: this.i18n.translate('app.settings.referrals.promotional.title'),
        description: this.i18n.translate('app.settings.referrals.promotional.description'),
        helpArticleKey: 'referralsPromotional',
        upgradeUrl: '/settings/referrals/promotional',
        mediaSourceType: MediaSourceType.YoutubeVideo,
      },
      [GuardedFeature.AuditLogs]: {
        mediaSource: 'assets/images/feature-guard/audit-logs.png',
        title: this.i18n.translate('app.pricing.feature_guard.audit_logs.title'),
        description: this.i18n.translate('app.pricing.feature_guard.audit_logs.description'),
        helpArticleKey: 'auditLogs',
        upgradeUrl: '/settings/upgrade/audit_logs',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.AuditLogs,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.AuditLogs),
        },
      },
      [GuardedFeature.FairEvaluations]: {
        mediaSource: 'https://www.youtube.com/embed/gnfR1cvlegA',
        title: this.i18n.translate('app.pricing.feature_guard.fair_evaluations.title'),
        description: this.i18n.translate('app.pricing.feature_guard.fair_evaluations.description'),
        helpArticleKey: 'fairEvaluations',
        mediaSourceType: MediaSourceType.YoutubeVideo,
        upgradeUrl: '',
        noContentWidth: true,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.FairEvaluations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.FairEvaluations,
            ),
        },
      },
      [GuardedFeature.CandidateAnonymization]: {
        mediaSource: 'https://www.youtube.com/embed/LdxGNNHSeOE?si=quPCptuqqJEzwl3g',
        title: this.i18n.translate('app.stages.candidate_anonymization.enable_dialog.heading'),
        description: this.i18n.translate(
          'app.stages.candidate_anonymization.enable_dialog.subheading',
        ),
        helpArticleKey: 'candidateAnonymization',
        mediaSourceType: MediaSourceType.YoutubeVideo,
        upgradeUrl: '',
        noContentWidth: true,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.CandidateAnonymization,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.CandidateAnonymization,
            ),
        },
      },
      [GuardedFeature.Dashboards]: {
        mediaSource: [
          'assets/images/feature-guard/new_dashboards_light.svg',
          'assets/images/feature-guard/new_dashboards_dark.svg',
        ],
        title: this.i18n.translate('app.pricing.feature_guard.dashboards.title'),
        description: this.i18n.translate('app.pricing.feature_guard.dashboards.description'),
        helpArticleKey: 'dashboards',
        mediaSourceType: MediaSourceType.Image,
        upgradeUrl: '',
        noContentWidth: true,
      },
      [GuardedFeature.AiWriter]: {
        mediaSource: [
          'assets/images/feature-guard/ai_writer_light.svg',
          'assets/images/feature-guard/ai_writer_dark.svg',
        ],
        title: this.i18n.translate('app.pricing.feature_guard.ai_writer.title'),
        description: this.i18n.translate('app.pricing.feature_guard.ai_writer.description'),
        helpArticleKey: 'aiWriter',
        mediaSourceType: MediaSourceType.Image,
        upgradeUrl: '',
        noContentWidth: true,
      },
      [GuardedFeature.AiJobContent]: {
        mediaSource: [
          'assets/images/feature-guard/ai_job_content_light.svg',
          'assets/images/feature-guard/ai_job_content_dark.svg',
        ],
        title: this.i18n.translate('app.pricing.feature_guard.ai_job_content.title'),
        description: this.i18n.translate('app.pricing.feature_guard.ai_job_content.description'),
        helpArticleKey: 'aiJobDescription',
        mediaSourceType: MediaSourceType.Image,
        upgradeUrl: '',
        noContentWidth: true,
      },
      [GuardedFeature.JobAiTranslation]: {
        mediaSource: [
          'assets/images/feature-guard/ai_job_translations_light.svg',
          'assets/images/feature-guard/ai_job_translations_dark.svg',
        ],
        title: this.i18n.translate('app.pricing.feature_guard.job_ai_translation.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.job_ai_translation.description',
        ),
        helpArticleKey: 'aiJobTranslations',
        mediaSourceType: MediaSourceType.Image,
        upgradeUrl: '',
        noContentWidth: true,
      },
      [GuardedFeature.JobTemplates]: {
        mediaSource: [
          'assets/images/feature-guard/job_templates_light.png',
          'assets/images/feature-guard/job_templates_dark.png',
        ],
        title: this.i18n.translate('app.pricing.feature_guard.job_templates.title'),
        description: this.i18n.translate('app.pricing.feature_guard.job_templates.description'),
        helpArticleKey: 'jobTemplates',
        mediaSourceType: MediaSourceType.Image,
        upgradeUrl: 'settings/upgrade/job_templates',
        noContentWidth: true,
      },
      [GuardedFeature.NotificationsPerRole]: {
        mediaSource: 'assets/images/feature-guard/notifications-per-role.png',
        title: this.i18n.translate('app.pricing.feature_guard.notifications_per_role.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.notifications_per_role.description',
        ),
        helpArticleKey: 'notificationsPerRole',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.NotificationsPerRole,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.NotificationsPerRole,
            ),
        },
      },
      [GuardedFeature.KnockoutQuestions]: {
        mediaSource: 'assets/images/feature-guard/knockout-questions.png',
        title: this.i18n.translate('app.pricing.feature_guard.knockout_questions.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.knockout_questions.description',
        ),
        helpArticleKey: 'knockoutQuestions',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.KnockoutQuestions,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.KnockoutQuestions,
            ),
        },
      },
      [GuardedFeature.Requisitions]: {
        mediaSource: 'assets/images/feature-guard/requisitions.svg',
        title: this.i18n.translate('app.pricing.feature_guard.requisitions.title'),
        description: this.i18n.translate('app.pricing.feature_guard.requisitions.description'),
        helpArticleKey: 'introductionToRequisitionApprovals',
        upgradeUrl: '/settings/upgrade/requisition_approvals',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Requisitions,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Requisitions),
        },
        noContentWidth: true,
      },
      [GuardedFeature.Locations]: {
        mediaSource: 'assets/images/feature-guard/locations.svg',
        title: this.i18n.translate('app.pricing.feature_guard.locations.title'),
        description: this.pp24Enabled
          ? this.i18n.translate('app.pricing.feature_guard.locations.description_new_pricing_plan')
          : this.i18n.translate('app.pricing.feature_guard.locations.description_old_pricing_plan'),
        helpArticleKey: 'createJobs',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.Locations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Locations),
        },
        noContentWidth: true,
      },
      [GuardedFeature.AcquisitionPaidCampaigns]: {
        title: '',
        description: '',
        upgradeUrl: '/dashboard/overview',
        mediaSourceType: MediaSourceType.Image,
        mediaSource: '',
      },
      [GuardedFeature.CustomFields]: {
        mediaSource: `assets/images/feature-guard/custom-fields-${this.themeName()}.svg`,
        title: this.i18n.translate('app.pricing.feature_guard.custom_fields.title'),
        description: this.i18n.translate('app.pricing.feature_guard.custom_fields.description'),
        helpArticleKey: 'customFields',
        upgradeUrl: '/settings/upgrade/custom_fields',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.CustomFields,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.CustomFields),
        },
        noContentWidth: true,
      },
      [FrontendOnlyGuardedFeature.UnlimitedCustomFields]: {
        mediaSource: `assets/images/feature-guard/custom-fields-${this.themeName()}.svg`,
        title: this.i18n.translate('app.pricing.feature_guard.unlimited_custom_fields.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.unlimited_custom_fields.description',
        ),
        helpArticleKey: 'customFields',
        upgradeUrl: '/settings/upgrade/custom_fields',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.CustomFields,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.CustomFields),
        },
        noContentWidth: true,
      },
      [GuardedFeature.OfferLetters]: {
        mediaSource: 'assets/images/feature-guard/offer-letters-2x.png',
        title: this.i18n.translate('app.pricing.feature_guard.offer_letters.title'),
        description: this.i18n.translate('app.pricing.feature_guard.offer_letters.description'),
        helpArticleKey: 'offerLetters',
        upgradeUrl: '/settings/upgrade/offer_letters',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.OfferLetters,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.OfferLetters),
        },
      },
      [FrontendOnlyGuardedFeature.UnlimitedJobPageLayouts]: {
        mediaSource: `assets/images/feature-guard/job-page-layouts-${this.themeName()}.svg`,
        title: this.i18n.translate('app.pricing.feature_guard.job_page_layouts.title'),
        description: this.i18n.translate('app.pricing.feature_guard.job_page_layouts.description'),
        helpArticleKey: 'jobPageLayouts',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.JobPageLayouts,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.JobPageLayouts,
            ),
        },
        noContentWidth: true,
      },
      [FrontendOnlyGuardedFeature.WorkflowAutomationsNewPlans]: {
        mediaSource: `assets/images/feature-guard/workflow-automations-${this.themeName()}.svg`,
        title: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.new_plans.title',
        ),
        description: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.new_plans.description',
        ),
        helpArticleKey: 'workflowAutomations',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
        },
      },
      [FrontendOnlyGuardedFeature.WorkflowAutomationsLaunchNewPlans]: {
        mediaSource: `assets/images/feature-guard/workflow-automations-${this.themeName()}.svg`,
        title: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.launch_new_plans.title',
        ),
        description: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.launch_new_plans.description',
        ),
        helpArticleKey: 'workflowAutomations',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
        },
      },
      [FrontendOnlyGuardedFeature.WorkflowAutomationsAdvance]: {
        mediaSource: `assets/images/feature-guard/workflow-automations-${this.themeName()}.svg`,
        title: this.i18n.translate('app.pricing.feature_guard.workflow_automations.advance.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.advance.description',
        ),
        helpArticleKey: 'workflowAutomations',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
        },
      },
      [FrontendOnlyGuardedFeature.WorkflowAutomationsOptimize]: {
        mediaSource: `assets/images/feature-guard/workflow-automations-${this.themeName()}.svg`,
        title: this.i18n.translate('app.pricing.feature_guard.workflow_automations.optimize.title'),
        description: this.i18n.translate(
          'app.pricing.feature_guard.workflow_automations.optimize.description',
        ),
        helpArticleKey: 'workflowAutomations',
        upgradeUrl: '',
        mediaSourceType: MediaSourceType.Image,
        analytics: {
          discrimination: () =>
            this.pricingAnalyticsService.trackFeatureDiscriminated(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
          cta: () =>
            this.pricingAnalyticsService.trackUpgradeCTAClick(
              PricingAnalyticsFeature.WorkflowAutomations,
            ),
        },
      },
    };
  }
}
